import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { IoMdArrowRoundBack } from "react-icons/io";
import './admin.css';

const RabbitAdmin = () => {
    //const API_BASE_URL = "http://localhost:3001/zajky";
    const API_BASE_URL = "https://zz.obn.sav.sk/zajky";
    const [activeView, setActiveView] = useState(null);
    const [rabbits, setRabbits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [currentRabbitId, setCurrentRabbitId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        age: '',
        sex: 'unknown',
        description: '',
        slogan: '',
        status: 'vet_prep',
        is_vaccinated: false,
        is_neutered: false,
        contact_preference: '0',
        images: [null, null, null],
        imagePreviews: ['', '', '']
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 5,
        total: 0,
        hasMore: true
    });
    const [searchTerm, setSearchTerm] = useState('');

    axios.defaults.withCredentials = true;

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/auth/check`, {
                    withCredentials: true,
                });
                if (response.status === 200) {
                    setIsLoggedIn(true);
                    // Remove fetchRabbits from here
                }
            } catch (err) {
                setIsLoggedIn(false);
                Cookies.remove('auth_token');
            }
        };

        verifyAuth();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/login`, loginData, {
                withCredentials: true,
            });
            if (response.status === 200) {
                setIsLoggedIn(true);
                // Remove fetchRabbits() from here
                setSuccessMessage('Login successful!');
                setTimeout(() => setSuccessMessage(''), 3000);
            }
        } catch (err) {
            setError('Login failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
                withCredentials: true
            });
            Cookies.remove('auth_token', { path: '/' });
            setIsLoggedIn(false);
            setRabbits([]);
        } catch (err) {
            console.error("Logout failed", err);
        }
    };


    const handleAgeChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^[\d.?]*$/.test(value)) {
            setFormData(prev => ({
                ...prev,
                age: value
            }));
        }
    };

    const fetchPaginatedRabbits = async (page = 1, limit = 6, search = '') => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/rabbits`, {
                params: {
                    page,
                    limit,
                    search
                }
            });

            setRabbits(prev =>
                page === 1
                    ? response.data.rabbits
                    : [...prev, ...response.data.rabbits]
            );

            setPagination({
                page,
                limit,
                total: response.data.totalCount,
                hasMore: (page * limit) < response.data.totalCount
            });

            setLoading(false);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
            setLoading(false);
        }
    };

    const searchRabbitsByName = async (name) => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/admin/rabbits/search`, {
                params: { name }
            });
            setRabbits(response.data); // Assuming this returns the full list of matches
            setPagination({
                page: 1,
                limit: response.data.length,
                total: response.data.length,
                hasMore: false
            });
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
            setLoading(false);
        }
    };

    const handleSearch = async (term) => {
        setSearchTerm(term);
        if (term.trim() === '') {
            // If search term is empty, fetch regular paginated rabbits
            await fetchPaginatedRabbits(1, pagination.limit);
        } else {
            // If there's a search term, use the search endpoint
            await searchRabbitsByName(term);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e, index) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const newImages = [...formData.images];
                const newPreviews = [...formData.imagePreviews];

                newImages[index] = file;
                newPreviews[index] = reader.result;

                setFormData(prev => ({
                    ...prev,
                    images: newImages,
                    imagePreviews: newPreviews
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const resetForm = () => {
        setIsEditing(false);
        setCurrentRabbitId(null);
        setFormData({
            name: '',
            age: '',
            sex: 'unknown',
            description: '',
            slogan: '',
            status: 'vet_prep',
            is_vaccinated: false,
            is_neutered: false,
            contact_preference: '0',
            images: [null, null, null],
            imagePreviews: ['', '', '']
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('age', formData.age);
            data.append('sex', formData.sex);
            data.append('description', formData.description);
            data.append('slogan', formData.slogan);
            data.append('status', formData.status);
            data.append('is_vaccinated', formData.is_vaccinated);
            data.append('is_neutered', formData.is_neutered);
            data.append('contact_preference', formData.contact_preference);

            formData.images.forEach((image, index) => {
                if (image) {
                    data.append(`image${index === 0 ? '' : index + 1}`, image);
                }
            });

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                withCredentials: true
            };

            if (isEditing && currentRabbitId) {
                await axios.put(`${API_BASE_URL}/api/admin/${currentRabbitId}`, data, config);
                setSuccessMessage('Rabbit updated successfully!');
            } else {
                await axios.post(`${API_BASE_URL}/api/admin`, data, config);
                setSuccessMessage('Rabbit added successfully!');
            }

            resetForm();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    const handleEdit = async (rabbit) => {
        try {
            setLoading(true);
            // Fetch full rabbit details
            const response = await axios.get(`${API_BASE_URL}/api/rabbits/${rabbit.rabbit_id}`);
            const fullRabbitDetails = response.data;

            setIsEditing(true);
            setActiveView('add');
            setCurrentRabbitId(rabbit.rabbit_id);

            const previews = [
                fullRabbitDetails.image_data ? `data:${fullRabbitDetails.image_mime_type};base64,${fullRabbitDetails.image_data}` : '',
                fullRabbitDetails.image_data2 ? `data:${fullRabbitDetails.image_mime_type2};base64,${fullRabbitDetails.image_data2}` : '',
                fullRabbitDetails.image_data3 ? `data:${fullRabbitDetails.image_mime_type3};base64,${fullRabbitDetails.image_data3}` : ''
            ];

            setFormData({
                name: fullRabbitDetails.name,
                age: fullRabbitDetails.age,
                sex: fullRabbitDetails.sex,
                description: fullRabbitDetails.description ?? '',
                slogan: fullRabbitDetails.slogan ?? '',
                status: fullRabbitDetails.status || 'vet_prep',
                is_vaccinated: fullRabbitDetails.is_vaccinated,
                is_neutered: fullRabbitDetails.is_neutered,
                contact_preference: fullRabbitDetails.contact_preference?.toString() || '0',
                images: [null, null, null],
                imagePreviews: previews
            });
        } catch (err) {
            setError('Failed to fetch rabbit details: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this rabbit?')) {
            try {
                await axios.delete(`${API_BASE_URL}/api/admin/${id}`, {
                    withCredentials: true
                });
                setSuccessMessage('Rabbit deleted successfully!');
                fetchPaginatedRabbits();
                setTimeout(() => setSuccessMessage(''), 3000);
            } catch (err) {
                setError(err.response?.data?.error || err.message);
            }
        }
    };

    const statusDisplay = {
        'vet_prep': 'With Vet',
        'looking_for_home': 'Looking for Home',
        'found_home': 'Adopted'
    };

    const contactDisplay = {
        '0': 'Marta',
        '1': 'Ja'
    };

    const handleView = () => {
        setActiveView('view');
        fetchPaginatedRabbits();
    };

    const LoadMoreButton = () => (
        <div className="load-more-container">
            {pagination.hasMore && (
                <button
                    onClick={() => fetchPaginatedRabbits(pagination.page + 1, pagination.limit, searchTerm)}
                    disabled={loading}
                    className="load-more-button"
                >
                    {loading ? 'Loading...' : 'Load More'}
                </button>
            )}
        </div>
    );

    const handleImageDelete = async (index) => {
        const imageKey = `image${index + 1}`; // image1, image2, image3

        if (isEditing && formData.imagePreviews[index] && currentRabbitId) {
            try {
                await axios.delete(`${API_BASE_URL}/api/admin/${currentRabbitId}/image/${imageKey}`, {
                    withCredentials: true,
                });
                setSuccessMessage(`Image ${index + 1} deleted.`);
                setTimeout(() => setSuccessMessage(''), 3000);
            } catch (err) {
                setError(`Failed to delete image ${index + 1}: ` + (err.response?.data?.error || err.message));
                return;
            }
        }

        // Update frontend state
        const updatedImages = [...formData.images];
        const updatedPreviews = [...formData.imagePreviews];
        updatedImages[index] = null;
        updatedPreviews[index] = '';

        setFormData(prev => ({
            ...prev,
            images: updatedImages,
            imagePreviews: updatedPreviews
        }));
    };

    if (loading) {
        return <div className="loading">Loading rabbits...</div>;
    }

    return (
        <div className="admin-container">


            {!isLoggedIn ? (
                <div className="login-panel-container">
                    <div className="login-panel">
                        <h1>Admin Login</h1>
                        <div className="admin-login-form-container">
                            <form onSubmit={handleLogin}>
                                <div className="form-group-admin">
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        value={loginData.username}
                                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group-admin">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="login-button-container"><button type="submit" className='login-button'>Login</button></div>
                            </form>
                        </div>
                        {error && <div className="error-message">{error}</div>}
                    </div>
                </div>
            ) : (
                <>
                    <h1 style={{ textAlign: 'center' }}>Admin Panel</h1>

                    {error && <div className="error-message">{error}</div>}
                    {successMessage && <div className="success-message">{successMessage}</div>}

                    {activeView === null && (
                        <div className="main-actions-container">
                            <div className="main-actions">
                                <button onClick={() => setActiveView('add')}>Add Rabbit</button>
                                <button onClick={() => handleView()}>View Rabbits</button>
                                <button className="logout-button" onClick={handleLogout}>Logout</button>
                            </div>
                        </div>
                    )}


                    {activeView === 'add' && (
                        <div className="form-container">
                            <button className="back-button" onClick={() => { resetForm(); setActiveView(null); }}><IoMdArrowRoundBack />Back</button>
                            <h2>{isEditing ? 'Edit Rabbit' : 'Add New Rabbit'}</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label>Name:</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Age:</label>
                                    <input
                                        type="text"
                                        name="age"
                                        value={formData.age}
                                        onChange={handleAgeChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sex:</label>
                                    <select
                                        name="sex"
                                        value={formData.sex}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="unknown">Unknown</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Status:</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="vet_prep">With Vet</option>
                                        <option value="looking_for_home">Looking for Home</option>
                                        <option value="found_home">Adopted</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Contact:</label>
                                    <select
                                        name="contact_preference"
                                        value={formData.contact_preference}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="0">Marta</option>
                                        <option value="1">Ja</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Description:</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slogan:</label>
                                    <input
                                        type="text"
                                        name="slogan"
                                        value={formData.slogan}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_vaccinated"
                                            checked={formData.is_vaccinated}
                                            onChange={handleInputChange}
                                        />
                                        Vaccinated
                                    </label>
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_neutered"
                                            checked={formData.is_neutered}
                                            onChange={handleInputChange}
                                        />
                                        Neutered
                                    </label>
                                </div>
                                {[0, 1, 2].map((index) => (
                                    <div key={index} className="form-group">
                                        <label>Image {index + 1}:</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(e, index)}
                                        />
                                        {formData.imagePreviews[index] && (
                                            <div className="image-preview">
                                                <img src={formData.imagePreviews[index]} alt={`Preview ${index + 1}`} />
                                                <button
                                                    type="button"
                                                    className="delete-image-button"
                                                    onClick={() => handleImageDelete(index)}
                                                >
                                                    Delete Image
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div className="form-actions">
                                    <button type="submit" className="save-button">
                                        {isEditing ? 'Update Rabbit' : 'Add Rabbit'}
                                    </button>
                                    {isEditing && (
                                        <button type="button" onClick={resetForm} className="cancel-button">
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    )}

                    {activeView === 'view' && (
                        <div className="rabbits-list">
                            <button className="back-button" onClick={() => setActiveView(null)}>
                                <IoMdArrowRoundBack /> Back
                            </button>
                            <h2>Current Rabbits</h2>

                            <div className="search-container">
                                <input
                                    type="text"
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch(searchTerm);
                                        }
                                    }}
                                />
                                <button
                                    onClick={() => handleSearch(searchTerm)}
                                    className="search-button"
                                >
                                    Search
                                </button>
                                {searchTerm && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            fetchPaginatedRabbits();
                                        }}
                                        className="clear-search-button"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {loading ? (
                                <div className="loading">Loading...</div>
                            ) : rabbits.length === 0 ? (
                                <p>No rabbits found{searchTerm ? ` matching "${searchTerm}"` : ''}.</p>
                            ) : (
                                <>
                                    <div className="rabbits-grid">
                                        {rabbits.map(rabbit => (
                                            <div key={rabbit.rabbit_id} className="rabbit-card">
                                                <div className="rabbit-image">
                                                    {rabbit.image_data ? (
                                                        <img
                                                            src={`data:${rabbit.image_mime_type};base64,${rabbit.image_data}`}
                                                            alt={rabbit.name}
                                                        />
                                                    ) : (
                                                        <div className="no-image">No Image</div>
                                                    )}
                                                </div>
                                                <div className="rabbit-details">
                                                    <h3>{rabbit.name}</h3>
                                                    <p><strong>Age:</strong> {rabbit.age}</p>
                                                    <p><strong>Sex:</strong> {rabbit.sex}</p>
                                                    <p><strong>Status:</strong> {statusDisplay[rabbit.status]}</p>
                                                    <p><strong>Contact:</strong> {contactDisplay[rabbit.contact_preference]}</p>
                                                    {/*{rabbit.slogan && <p><strong>Slogan:</strong> {rabbit.slogan}</p>}
                                                    <p><strong>Vaccinated:</strong> {rabbit.is_vaccinated ? 'Yes' : 'No'}</p>
                                                    <p><strong>Neutered:</strong> {rabbit.is_neutered ? 'Yes' : 'No'}</p>
                                                    {rabbit.description && (
                                                        <p><strong>Description:</strong> {rabbit.description}</p>
                                                    )}*/}
                                                </div>
                                                <div className="rabbit-actions">
                                                    <button
                                                        onClick={() => handleEdit(rabbit)}
                                                        className="edit-button"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(rabbit.rabbit_id)}
                                                        className="delete-button"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Only show load more button when not in search mode and there are more rabbits to load */}
                                    {!searchTerm && pagination.hasMore && (
                                        <div className="load-more-container">
                                            <button
                                                onClick={() => fetchPaginatedRabbits(pagination.page + 1, pagination.limit)}
                                                disabled={loading}
                                                className="load-more-button"
                                            >
                                                {loading ? 'Loading...' : 'Load More'}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default RabbitAdmin;